// src/hooks/usePricingAccess.js
// T6-PRICING-PANEL-01. Whether the signed-in staff member may see the Pricing tab.
//
// The pricing role is united_ai admin, enforced by mgmt-backend's requireStaffAccess on
// every /api/united-pricing route. The panel cannot read the role directly in production
// (GET /api/employee/:uid is behind the legacy gate, so useGtmAdmin answers false there),
// so this asks the gate itself with the sources read and hides the tab unless it was let
// through (accessFromResponse in pricingPanel.js). Fails closed: 'checking' and 'denied'
// both hide the tab. One probe per signed-in user, shared by the sidebar and the page.

import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { pricingApi, accessFromError } from '../pages/enterprise-ai/pricingPanel';

const probes = new Map(); // uid -> Promise<'allowed'|'denied'>

export function probePricingAccess(uid, read = pricingApi.sources) {
    if (!probes.has(uid)) {
        const p = read()
            .then(() => 'allowed')
            .catch((err) => {
                const access = accessFromError(err);
                if (access === 'denied') probes.delete(uid); // a later sign-in or deploy is asked again
                return access;
            });
        probes.set(uid, p);
    }
    return probes.get(uid);
}

// Test-only.
export function _resetPricingAccess() {
    probes.clear();
}

export function usePricingAccess() {
    const [access, setAccess] = useState('checking');
    useEffect(() => {
        let live = true;
        let req = 0;
        const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
            const mine = ++req;
            if (!user) {
                if (live) setAccess('denied');
                return;
            }
            setAccess('checking');
            probePricingAccess(user.uid).then((a) => {
                if (live && mine === req) setAccess(a);
            });
        });
        return () => { live = false; unsubscribe(); };
    }, []);
    return access;
}
